import {
  type EntityId,
  gatherResource,
  isCity,
  type PlayerId,
  type SimContext,
  type SimState,
} from '../../../sim';
import { type GameEvent, hexDistance } from '../types';

/**
 * The player's automated workers that found no work (their last search came
 * up empty and they aren't gathering, building or on the way), listed by
 * home city: the `homeCityId` city while it's theirs, else their nearest
 * completed city (lowest id on a tie). Ids are in unit mint order.
 */
export function idleWorkerIds(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId
): Map<EntityId, EntityId[]> {
  const ids = new Map<EntityId, EntityId[]>();
  const cities = Object.values(state.buildings)
    .filter((b) => b.owner === playerId && isCity(b, ctx.rules) && b.buildProgress >= 1)
    .sort((a, b) => Number(a.id.slice(1)) - Number(b.id.slice(1)));
  if (cities.length === 0) return ids;
  for (const u of Object.values(state.units)) {
    // A retryTick in the past is stale (only a new path clears it): not idle.
    const retryTick = u.auto?.retryTick;
    if (u.owner !== playerId || retryTick === undefined || retryTick < state.tick || u.inside)
      continue;
    if (ctx.rules.units[u.type]?.automate !== 'work') continue;
    if ((u.path && u.path.length > 0) || u.order) continue;
    if (gatherResource(state, ctx, u)) continue;
    const home = state.buildings[u.homeCityId ?? ''];
    let city = home?.owner === playerId && isCity(home, ctx.rules) ? home : undefined;
    if (!city) {
      let best = Number.POSITIVE_INFINITY;
      for (const c of cities) {
        const d = hexDistance(u.pos, c.pos);
        if (d < best) {
          best = d;
          city = c;
        }
      }
    }
    if (!city) continue;
    const list = ids.get(city.id);
    if (list) list.push(u.id);
    else ids.set(city.id, [u.id]);
  }
  return ids;
}

/** `idleWorkerIds` counted per home city. */
export function idleWorkers(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId
): Map<EntityId, number> {
  const counts = new Map<EntityId, number>();
  for (const [city, ids] of idleWorkerIds(state, ctx, playerId)) counts.set(city, ids.length);
  return counts;
}

/**
 * "Workers idle in City 2: build farms or forestries" (count chip ×6), raised when a
 * city's idle count rises above the most already reported for it. `reported`
 * holds those counts (updated in place; a city back to none is forgotten, so
 * the next idle spell is reported afresh).
 */
export function idleEvents(
  state: SimState,
  counts: ReadonlyMap<EntityId, number>,
  reported: Map<EntityId, number>,
  cityLabel: (cityId: EntityId) => string
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const id of [...reported.keys()]) if (!counts.has(id)) reported.delete(id);
  for (const [id, n] of counts) {
    if (n <= (reported.get(id) ?? 0)) continue;
    reported.set(id, n);
    const city = state.buildings[id];
    events.push({
      kind: 'idle',
      text: `${n === 1 ? 'Worker' : 'Workers'} idle in ${cityLabel(id)}: build farms or forestries`,
      idle: n,
      ...(city ? { pos: { ...city.pos } } : {}),
      cityId: id,
    });
  }
  return events;
}
